import { SsidChart, ZoomInMap, ZoomOutMap } from '@mui/icons-material';
import { Box, IconButton, Modal, Typography } from '@mui/material';
import { BarElement, Chart, ChartOptions } from 'chart.js';
import { useMemo, useState } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { formatCurrency } from '@utils/common';
import { customChartTooltipHandler } from '../../../pages/Dashboard/components/Underwritting/Charts/utils';

Chart.register(BarElement);

interface DashboardChartCardProps {
  title: string;
  description: string;
  data: any;
  type?: 'line' | 'bar';
  format?: 'percentage' | 'currency' | 'number';
  showLegend?: boolean;
  showTitle?: boolean;
}

const EMPTY_CHART_DATA = { labels: [] as string[], datasets: [] as any[] };

const DashboardChartCard = ({
  title,
  description,
  data,
  type = 'line',
  format,
  showLegend,
  showTitle = true,
}: DashboardChartCardProps) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const ChartComponent = type === 'bar' ? Bar : Line;
  const chartData =
    data && !Array.isArray(data) && Array.isArray(data.datasets) ? data : EMPTY_CHART_DATA;
  const chartOptions: ChartOptions<'line' | 'bar'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: true,
      interaction: { intersect: type === 'bar' },
      elements:
        type === 'bar'
          ? {
              bar: {
                borderRadius: 4,
              },
            }
          : {
              line: {
                borderCapStyle: 'round',
                borderJoinStyle: 'round',
              },
              point: {
                radius: 0,
                hoverRadius: 0,
              },
            },
      scales: {
        x: {
          grid: {
            display: false,
          },
          border: {
            color: '#AECBD0',
          },
          display: true,
          title: {
            text: '',
            display: false,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            display: true,
            color: '#E8EAEB',
            tickWidth: 0,
          },
          border: {
            color: '#AECBD0',
            dash: [5, 5],
          },
          ticks: {
            ...(format === 'number' ? { stepSize: 1, precision: 0 } : {}),
            callback: function (value) {
              if (format === 'percentage') return `${Number(value)}%`;
              if (format === 'currency') return formatCurrency(value) || '$0';
              return `${Math.round(Number(value))}`;
            },
          },
        },
      },
      plugins: {
        legend: {
          display: false,
          position: 'bottom',
          align: 'start',
          labels: {
            padding: 16,
            color: '#6C6C6C',
            font: {
              family: 'Manrope',
              size: 12,
              weight: 600,
            },
            boxWidth: 8,
            boxHeight: 8,
            useBorderRadius: true,
            borderRadius: 4,
            usePointStyle: true,
            pointStyle: 'circle',
            generateLabels: (chart: any) => {
              const defaultLabels = Chart.defaults.plugins.legend.labels.generateLabels(chart);
              return defaultLabels?.map((label: any) => {
                if (label.datasetIndex != null) {
                  const dataset = chart.data.datasets[label.datasetIndex];
                  label.fillStyle = dataset.borderColor || dataset.backgroundColor;
                  label.strokeStyle = dataset.borderColor || dataset.backgroundColor;
                }
                return label;
              });
            },
          },
        },
        tooltip: {
          enabled: false,
          external: (context) => {
            const chartData = context.chart.data.datasets as any[];
            const hoveredDataPoint = context.tooltip?.dataPoints?.[0];
            const dataIndex = hoveredDataPoint?.dataIndex;
            const datasetIndex = hoveredDataPoint?.datasetIndex;

            if (dataIndex == null || datasetIndex == null) return;

            // Get the hovered dataset and its y value
            const hoveredDataset = chartData[datasetIndex];
            if (!hoveredDataset || hoveredDataset?.data?.[dataIndex] == null) return;

            const hoveredYValue = hoveredDataset.data[dataIndex];

            // Find all datasets with the same x index and same y value
            const tooltipData = chartData
              ?.map((dataset: any, idx: number) => {
                const raw = dataset.data?.[dataIndex];
                // Include datasets that have the same x (dataIndex) and same y value
                if (raw == null || raw !== hoveredYValue) return null;

                const formattedValue =
                  format === 'percentage'
                    ? `${Number(raw).toFixed(2)}%`
                    : format === 'currency'
                      ? formatCurrency(raw) || '$0'
                      : `${Math.round(Number(raw))}`;
                let backgroundColor: any = dataset.backgroundColor;
                try {
                  if (typeof backgroundColor === 'function') {
                    backgroundColor = backgroundColor({
                      chart: context.chart,
                      dataIndex,
                      datasetIndex: idx,
                      type: 'data',
                    });
                  }
                } catch {}
                return {
                  backgroundColor: backgroundColor ?? '#2F736E',
                  label: dataset.label,
                  value: formattedValue,
                  rawValue: raw,
                  style: 'font-weight: 600;',
                } as any;
              })
              .filter((item: any) => item != null);

            customChartTooltipHandler({
              context,
              title: '',
              containerId: 'pvx-financial-projections-container',
              tooltipData,
              tooltipConfigs: {
                bodyConfigs: [{ type: 'indicator' }, { type: 'label' }, { type: 'value' }],
              } as any,
              yAxisTransformer: (v: any) =>
                format === 'percentage'
                  ? `${Number(v).toFixed(2)}%`
                  : format === 'currency'
                    ? formatCurrency(v) || '$0'
                    : `${Math.round(Number(v))}`,
              xAxisTransformer: (v: any) => v,
              xAxisLabel: '',
            });
          },
        },
      },
    }),
    [format, type],
  );
  const modalChartOptions = useMemo(
    () => ({
      ...chartOptions,
      maintainAspectRatio: false,
      plugins: {
        ...chartOptions.plugins,
        legend: {
          ...chartOptions.plugins?.legend,
          display: showLegend,
        },
      },
    }),
    [chartOptions, showLegend],
  );
  const DashboardChartCardHeader = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '4px',
          padding: '8px',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '4px',
            width: '100%',
            paddingBottom: '8px',
            borderBottom: '1px solid #E8EAEB',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {title?.trim() && (
              <>
                <SsidChart sx={{ color: '#162C36', width: '16px', height: '16px' }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#162936' }}>
                  {title}
                </Typography>
              </>
            )}
          </Box>
          {isFullScreen ? (
            <IconButton onClick={() => setIsFullScreen(false)}>
              <ZoomInMap sx={{ color: '#2F736E', width: '16px', height: '16px' }} />
            </IconButton>
          ) : (
            <IconButton onClick={() => setIsFullScreen(true)}>
              <ZoomOutMap sx={{ color: '#2F736E', width: '16px', height: '16px' }} />
            </IconButton>
          )}
        </Box>
      </Box>
    );
  };
  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '4px',
        border: '1px solid #E8EAEB',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        width: '100%',
        minWidth: '100px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <DashboardChartCardHeader />
      </Box>
      <ChartComponent data={chartData} options={chartOptions} />
      <Typography
        sx={{
          fontSize: '10px',
          fontWeight: 700,
          color: '#162936',
          width: '100%',
          textAlign: 'center',
        }}
      >
        {description}
      </Typography>
      <Modal
        open={isFullScreen}
        onClose={() => setIsFullScreen(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '80%',
            maxWidth: '1200px',
            height: '60vh',
            minHeight: '500px',
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            padding: '0.785rem',
            overflow: 'hidden',
            outline: 'none',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DashboardChartCardHeader />
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              position: 'relative',
              width: '100%',
            }}
          >
            <ChartComponent data={chartData} options={modalChartOptions} />
          </Box>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 700,
              color: '#162936',
              width: '100%',
              textAlign: 'center',
            }}
          >
            {description}
          </Typography>
        </Box>
      </Modal>
    </Box>
  );
};

export default DashboardChartCard;
